'use client'

import type { StaticImport } from 'next/dist/shared/lib/get-img-props'
import type { HTMLAttributeAnchorTarget } from 'react'
import { useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { LogIn, Menu } from 'lucide-react'
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from 'motion/react'

import { resetAnalytics } from '@package/analytics/client'
import PiBoLogo from '@ui/assets/logo/logo.svg'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ui/web/components'
import { buildUrl } from '@helper/utils/format'
import { useMediaQuery } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

import { authClient } from '~/auth/client'
import { useHasStickyBanner } from '~/context'
import { poppins } from '~/font'
import { DropdownNavButton } from './dropdown-nav-button'
import { NavDesktopButton } from './nav-button'
import { SignInModal } from './sign-in-modal'
import { SignUpModal } from './sign-up-modal'

interface NavLink {
  link: string
  name: string
  target?: HTMLAttributeAnchorTarget
}

const NAV_LINKS: NavLink[] = [
  {
    link: buildUrl('marketing', '/sekolah').href,
    name: 'Sekolah',
  },
  {
    link: buildUrl('marketing', '/orang-tua').href,
    name: 'Orang Tua',
  },
  {
    link: buildUrl('marketing', '/explore').href,
    name: 'Jelajahi',
  },
  {
    link: buildUrl('marketing', '/dampak').href,
    name: 'Dampak',
  },
  {
    link: buildUrl('marketing', '/blog/semua').href,
    name: 'Blog',
  },
]

const SCROLL_THRESHOLDS = {
  show: 10,
  hide: 150,
} as const

const ANIMATION_SCALES = {
  hover: 1.05,
  tap: 0.95,
} as const

const NAV_ANIMATION = {
  duration: 0.35,
  ease: 'easeInOut' as const,
} as const

interface AuthenticatedActionsProps {
  isDropdownMenuItem?: boolean
}

function AuthenticatedActions({ isDropdownMenuItem }: AuthenticatedActionsProps) {
  const handleSignOut = async () => {
    // Clear PostHog identity so the next user on a shared device starts clean.
    resetAnalytics()
    await authClient.signOut()
  }

  return (
    <div className='flex flex-wrap items-center gap-4'>
      <Button
        onClick={handleSignOut}
        variant='ghost'
        className={cn(isDropdownMenuItem && 'w-full flex-1 shrink-0 flex-nowrap text-nowrap')}
      >
        Keluar
      </Button>

      <Button
        variant='outline'
        className={cn(isDropdownMenuItem && 'w-full flex-1 shrink-0 flex-nowrap text-nowrap')}
        asChild
      >
        <Link href={buildUrl('account', '/check-user')} prefetch>
          Menuju aplikasi <LogIn className='ml-2 h-4 w-4 shrink-0' />
        </Link>
      </Button>
    </div>
  )
}

interface UnauthenticatedActionsProps {
  onSignInClick: () => void
  onSignupClick: () => void
  isDropdownMenuItem?: boolean
}

function UnauthenticatedActions({ onSignInClick, onSignupClick, isDropdownMenuItem }: UnauthenticatedActionsProps) {
  return (
    <div className='flex flex-wrap items-center gap-4'>
      <Button
        onClick={onSignInClick}
        variant='outline'
        className={cn(isDropdownMenuItem && 'w-full flex-1 shrink-0 flex-nowrap text-nowrap')}
      >
        Login
      </Button>

      <Button
        onClick={onSignupClick}
        className={cn(isDropdownMenuItem && 'w-full flex-1 shrink-0 flex-nowrap text-nowrap')}
      >
        Daftar
      </Button>
    </div>
  )
}

interface NavbarProps {
  className?: string
}

export function Navbar({ className }: NavbarProps) {
  const { scrollY } = useScroll()
  const { hasStickyBanner } = useHasStickyBanner()
  const [hidden, setHidden] = useState(false)
  const [openDropdown, setOpenDropdown] = useState(false)
  const [openSignInModal, setOpenSignInModal] = useState(false)
  const [openSignUpModal, setOpenSignUpModal] = useState(false)
  const { data: session } = authClient.useSession()

  const isMd = useMediaQuery('(min-width: 970px)', { initializeWithValue: false })

  useMotionValueEvent(scrollY, 'change', (latest) => {
    const previous = scrollY.getPrevious()
    if (latest < SCROLL_THRESHOLDS.show) {
      setHidden(false)
    } else if (previous && latest > previous && latest > SCROLL_THRESHOLDS.hide) {
      setHidden(true)
    } else {
      setHidden(false)
    }
  })

  return (
    <>
      <motion.nav
        variants={{
          visible: { y: 0 },
          hidden: { y: hasStickyBanner ? '-200%' : '-100%' },
        }}
        animate={hidden ? 'hidden' : 'visible'}
        transition={NAV_ANIMATION}
        className={cn(
          'bg-neutral-white border-outline-variant fixed top-0 right-0 left-0 z-50 flex h-16 w-dvw items-center justify-center border-b px-6 shadow-lg',
          className,
          poppins.className,
        )}
      >
        <section className='mx-auto flex w-full items-center justify-between gap-4 px-4'>
          <section className='flex items-center gap-x-4'>
            <Link href='/' className='h-fit w-fit shrink-0'>
              <Image src={PiBoLogo as StaticImport} alt='PiBo Logo' width={48} height={48} draggable={false} />
            </Link>

            {isMd && (
              <div className='flex items-center gap-x-5'>
                {NAV_LINKS.map((link) => (
                  <NavDesktopButton key={link.link} href={link.link} text={link.name} target={link.target} />
                ))}
              </div>
            )}
          </section>

          {isMd ? (
            session ? (
              <AuthenticatedActions />
            ) : (
              <UnauthenticatedActions
                onSignInClick={() => setOpenSignInModal(true)}
                onSignupClick={() => setOpenSignUpModal(true)}
              />
            )
          ) : (
            <AnimatePresence>
              <DropdownMenu
                open={openDropdown}
                onOpenChange={(open) => {
                  if (!open && (openSignInModal || openSignUpModal)) return
                  setOpenDropdown(open)
                }}
              >
                <DropdownMenuTrigger
                  render={(props) => (
                    <button type='button' {...props}>
                      <motion.div
                        whileHover={{ scale: ANIMATION_SCALES.hover }}
                        whileTap={{ scale: ANIMATION_SCALES.tap }}
                      >
                        <Menu className='text-neutral-black h-8 w-8' />
                      </motion.div>
                    </button>
                  )}
                />
                <DropdownMenuContent className='w-70'>
                  {NAV_LINKS.map((link) => (
                    <DropdownNavButton key={link.link} href={link.link} text={link.name} target={link.target} />
                  ))}
                  <DropdownMenuSeparator />
                  <div className='px-2 py-1'>
                    {session ? (
                      <AuthenticatedActions isDropdownMenuItem />
                    ) : (
                      <UnauthenticatedActions
                        isDropdownMenuItem
                        onSignInClick={() => setOpenSignInModal(true)}
                        onSignupClick={() => setOpenSignUpModal(true)}
                      />
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </AnimatePresence>
          )}
        </section>
      </motion.nav>

      <SignInModal open={openSignInModal} onOpenChange={setOpenSignInModal} className={poppins.className} />

      <SignUpModal open={openSignUpModal} onOpenChange={setOpenSignUpModal} className={poppins.className} />
    </>
  )
}

export { SignUpModal }
